// src/pages/Insights/Articles/ArticlesPage.tsx
// Clean, Google-inspired articles listing page

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../../components/SEO/SEO';
import { seoConfig } from '../../../config/seo';
import {
  Clock, Calendar,
  ArrowRight, FileText
} from 'lucide-react';
import { m } from 'framer-motion';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Typography } from '@/components/ui/typography';

// Import all post data
import { allPosts } from './ArticleData';

// Import shared types
import { Article } from '../types';

const ArticlesPage: React.FC = () => {


  // Sort posts by date
  const sortedPosts: Article[] = [...allPosts].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });

  // Get post URL - Flat structure
  const getPostUrl = (post: Article) => `/insights/articles/${post.slug}`;

  return (
    <>
      <SEO {...seoConfig.articles} />

      <div className="max-w-full mx-auto p-0 font-brand text-[#202124] bg-white min-h-screen">
        {/* Hero Section */}
        <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
          <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
            Engineering knowledge,{' '}<span className="text-[#1a73e8]">written to last.</span>
          </Typography>
          <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
            Peer-reviewed long-form articles on diff algorithms, PDF architecture, image compression, OCR, and modern file engineering — by practising engineers, free forever.
          </Typography>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/insights/courses"
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a73e8] text-white font-medium rounded-full no-underline hover:bg-[#1557b0] transition-colors shadow-sm"
            >
              Explore courses <ArrowRight size={17} />
            </Link>
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 px-7 py-3 bg-white border border-[#dadce0] text-[#3c4043] font-medium rounded-full no-underline hover:bg-[#f8f9fa] transition-colors"
            >
              Back to Insights
            </Link>
          </div>
        </header>

        {/* Stat strip */}
        <section className="max-w-7xl mx-auto px-6 pb-20 border-b border-[#f1f3f4]">
          <div className="flex justify-center gap-12 sm:gap-24 flex-wrap">
            {[
              { value: String(allPosts.length), label: 'In-Depth Articles' },
              { value: '100%', label: 'Free, No Paywall' },
              { value: '0', label: 'Account Required' },
              { value: '∞', label: 'Always Updated' },
            ].map(s => (
              <div key={s.label} className="flex flex-col items-center text-center">
                <div className="text-[2.75rem] font-light text-[#121317] leading-none mb-2">{s.value}</div>
                <div className="text-[0.875rem] text-[#5f6368]">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 pt-12 pb-24">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#e8f0fe] text-[#1a73e8]">
                  <FileText size={18} strokeWidth={1.5} />
                </div>
                <span className="text-[0.8rem] font-semibold uppercase tracking-widest text-[#1a73e8]">Engineering Guides</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Latest from the blog</h2>
              <p className="text-[#5f6368] text-lg leading-relaxed">In-depth technical articles, tutorials, and engineering best practices from our core team.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sortedPosts.map((post, index) => (
              <m.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: (index % 2) * 0.1, ease: [0.21, 0.45, 0.32, 0.9] }}
              >
                <Link
                  to={getPostUrl(post)}
                  className="flex flex-row gap-6 p-6 bg-white border border-[#e8eaed] rounded-[32px] hover:shadow-lg transition-all duration-300 no-underline group h-full"
                >
                  <div className="w-[100px] sm:w-[150px] aspect-square rounded-2xl overflow-hidden bg-[#f8f9fa] shrink-0 self-center">
                    <OptimizedImage
                      src={post.imageUrl}
                      srcSet={post.srcSet}
                      placeholder={post.placeholder}
                      alt={post.title}
                      width={190}
                      height={190}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading={index < 2 ? "eager" : "lazy"}
                      priority={index < 2}
                      cinematic
                    />
                  </div>
                  <div className="flex flex-col justify-between flex-1">
                    <div>
                      <span className="text-[0.75rem] font-bold text-[#1a73e8] uppercase tracking-wider mb-2 block">
                        {post.category?.replace(/-/g, ' ') || 'Engineering'}
                      </span>
                      <h3 className="text-xl font-medium text-[#121317] mb-2 group-hover:text-[#1a73e8] transition-colors leading-snug">
                        {post.title}
                      </h3>
                      <p className="text-[0.9rem] text-[#5f6368] leading-relaxed line-clamp-2">
                        {post.excerpt}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4">
                      <div className="flex items-center gap-1.5 text-[0.8rem] text-[#5f6368]">
                        <Clock size={14} strokeWidth={1.5} />
                        <span>{post.readTime}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[0.8rem] text-[#5f6368]">
                        <Calendar size={14} strokeWidth={1.5} />
                        <span>{post.date}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </m.div>
            ))}
          </div>
        </div>

      </div>
    </>
  );
};

export default ArticlesPage;
